import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';
import { BsToggleOff, BsToggleOn } from 'react-icons/bs';
import {
  FiGlobe, FiLock, FiMoreVertical, FiRotateCcw, FiTrash2
} from 'react-icons/fi';
import { RiIncreaseDecreaseLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { match } from 'ts-pattern';

import { AgentScaling } from '@/app/agents/agentScaling/agentScaling';
import { Button } from '@/components/button/button';
import { DeleteModal } from '@/components/deleteModal/deleteModal';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import {
  useAgentDelete,
  useAgentMakePrivate,
  useAgentMakePublic,
  useAgentRestart,
  useAgentScale,
  useAgentTurnOff,
  useAgentTurnOn,
  useByoaAgentDelete
} from '@/hooks/mutations/useAgentMutations';
import { agentsKeys } from '@/hooks/queries/useAgents';
import { byoaAgentsKeys } from '@/hooks/queries/useByoaAgents';
import type { ByoaAgent } from '@/types/agent';

import styles from './agentActionMenu.module.scss';

type Props = {
  agentId: string;
  agentName: string;
  isAidp: boolean;
  isPublic: boolean;
  isOff: boolean;
  byoaAgent?: ByoaAgent;
};

type ConfirmAction = 'makePublic' | 'turnOff' | 'restart';

export const AgentActionMenu = ( { agentId, agentName, isAidp, isPublic, isOff, byoaAgent }: Props ) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [ pendingConfirm, setPendingConfirm ] = useState<ConfirmAction | null>( null );
  const [ showDelete, setShowDelete ] = useState( false );
  const [ showScaling, setShowScaling ] = useState( false );

  const deleteMutation = useAgentDelete();
  const byoaDeleteMutation = useByoaAgentDelete();
  const makePublicMutation = useAgentMakePublic();
  const makePrivateMutation = useAgentMakePrivate();
  const scaleMutation = useAgentScale();
  const restartMutation = useAgentRestart();
  const turnOffMutation = useAgentTurnOff();
  const turnOnMutation = useAgentTurnOn();

  const handleMakePrivate = () => {
    makePrivateMutation.mutate( { agentId }, {
      onSuccess: () => toast.success( 'Agent made private' ),
      onError: () => toast.error( 'Unable to make agent private' )
    } );
  };

  const handleTurnOn = () => {
    turnOnMutation.mutate( { agentId }, {
      onSuccess: () => toast.success( 'Agent turned on' ),
      onError: () => toast.error( 'Unable to turn on agent' )
    } );
  };

  const handleConfirm = () => {
    match( pendingConfirm )
      .with( 'makePublic', () => makePublicMutation.mutate( { agentId }, {
        onSuccess: () => toast.success( 'Agent made public' ),
        onError: () => toast.error( 'Unable to make agent public' )
      } ) )
      .with( 'turnOff', () => turnOffMutation.mutate( { agentId }, {
        onSuccess: () => toast.success( 'Agent turned off' ),
        onError: () => toast.error( 'Unable to turn off agent' )
      } ) )
      .with( 'restart', () => restartMutation.mutate( { agentId }, {
        onSuccess: () => toast.success( 'Agent restarted' ),
        onError: () => toast.error( 'Unable to restart agent' )
      } ) )
      .with( null, () => {} )
      .exhaustive();
    setPendingConfirm( null );
  };

  const renderConfirmModal = () => {
    if ( !pendingConfirm ) return null;

    const { title, body, confirmLabel } = match( pendingConfirm )
      .with( 'makePublic', () => ( {
        title: 'Make agent public?',
        body: <p className={ styles.modalBody }>This agent will be visible to all users.</p> as ReactNode,
        confirmLabel: 'Make Agent Public'
      } ) )
      .with( 'turnOff', () => ( {
        title: 'Turn off agent?',
        body: (
          <>
            <p className={ styles.modalBody }>This will stop all requests to <strong>{ agentName }</strong> until it is turned back on.</p>
            <p className={ styles.modalBodySecondary }>Note: This agent may still be processing requests. View Agent Details to see recent activity.</p>
          </>
        ) as ReactNode,
        confirmLabel: 'Turn Off Agent'
      } ) )
      .with( 'restart', () => ( {
        title: 'Restart agent?',
        body: (
          <>
            <p className={ styles.modalBody }>This will restart the agent&apos;s running instances.</p>
            <p className={ styles.modalBodySecondary }>In-flight requests may be briefly interrupted.</p>
          </>
        ) as ReactNode,
        confirmLabel: 'Restart Agent'
      } ) )
      .exhaustive();

    return (
      <Modal>
        <Heading as="h2" size="md">{ title }</Heading>
        { body }
        <div className={ styles.modalActions }>
          <Button variant='secondary' size='sm' onClick={ () => setPendingConfirm( null ) }>Cancel</Button>
          <Button variant='primary' size='sm' onClick={ handleConfirm }>{ confirmLabel }</Button>
        </div>
      </Modal>
    );
  };

  return (
    <>
      <Menu
        icon={ <Icon icon={ FiMoreVertical } /> }
        variant='menu'
        data-testid="overflow-menu"
      >
        {isAidp && (
          <MenuItem
            key="scale"
            value="Scale"
            onClick={ () => setShowScaling( true ) }
            data-testid="menu-item"
            leadingIcon={ <Icon icon={ RiIncreaseDecreaseLine } size="sm" /> }
          />
        )}
        <MenuItem
          key="visibility"
          value={ isPublic ? 'Make Private' : 'Make Public' }
          onClick={ isPublic ? handleMakePrivate : () => setPendingConfirm( 'makePublic' ) }
          data-testid="menu-item"
          leadingIcon={ <Icon icon={ isPublic ? FiLock : FiGlobe } size="sm" /> }
        />
        {isAidp && ( isOff
          ? (
              <MenuItem
                key="power"
                value="Turn On"
                onClick={ handleTurnOn }
                data-testid="menu-item"
                leadingIcon={ <Icon icon={ BsToggleOff } size="sm" /> }
              />
            )
          : (
              <MenuItem
                key="power"
                value="Turn Off"
                onClick={ () => setPendingConfirm( 'turnOff' ) }
                data-testid="menu-item"
                leadingIcon={ <Icon icon={ BsToggleOn } size="sm" /> }
              />
            ) )}
        {isAidp && !isOff && (
          <MenuItem
            key="restart"
            value="Restart"
            onClick={ () => setPendingConfirm( 'restart' ) }
            data-testid="menu-item"
            leadingIcon={ <Icon icon={ FiRotateCcw } size="sm" /> }
          />
        )}
        <MenuItem
          key="delete"
          value="Delete"
          onClick={ () => setShowDelete( true ) }
          data-testid="menu-item"
          leadingIcon={ <Icon icon={ FiTrash2 } size="sm" /> }
        />
      </Menu>
      { renderConfirmModal() }
      <DeleteModal
        isOpen={ showDelete }
        onClose={ () => {
          setShowDelete( false );
          const succeeded = isAidp ? byoaDeleteMutation.isSuccess : deleteMutation.isSuccess;
          if ( succeeded ) {
            queryClient.invalidateQueries( { queryKey: agentsKeys.list() } );
            queryClient.removeQueries( { queryKey: agentsKeys.detail( agentId ) } );
            if ( isAidp ) {
              queryClient.invalidateQueries( { queryKey: byoaAgentsKeys.list() } );
              queryClient.removeQueries( { queryKey: byoaAgentsKeys.detail( byoaAgent?.id ?? '' ) } );
            }
            toast.success( 'Agent deleted' );
            navigate( '/agents' );
          }
          deleteMutation.reset();
          byoaDeleteMutation.reset();
        } }
        onDelete={ async() => {
          if ( isAidp ) {
            await byoaDeleteMutation.mutateAsync( { agentId: byoaAgent?.id ?? '' } );
          } else {
            await deleteMutation.mutateAsync( { agentId } );
          }
        } }
        entityName={ agentName }
        entityType="agent"
      />
      {showScaling && (
        <AgentScaling
          initialConfig={ byoaAgent ? {
            autoScaling: byoaAgent.autoScalingConfig?.enabled ?? false,
            instances: byoaAgent.replicas ?? 0,
            minInstances: byoaAgent.autoScalingConfig?.minReplicas ?? 1,
            maxInstances: byoaAgent.autoScalingConfig?.maxReplicas ?? 2,
            cpuUtilizationEnabled: byoaAgent.autoScalingConfig?.targetCpuUtilization !== undefined,
            targetCpuUtilization: byoaAgent.autoScalingConfig?.targetCpuUtilization ?? 75,
            memoryUtilizationEnabled: byoaAgent.autoScalingConfig?.targetMemoryUtilization !== undefined,
            targetMemoryUtilization: byoaAgent.autoScalingConfig?.targetMemoryUtilization ?? 75
          } : undefined }
          onApply={ ( config ) => scaleMutation.mutate(
            { agentId, ...config },
            { onSettled: () => setShowScaling( false ) }
          ) }
          onCancel={ () => setShowScaling( false ) }
        />
      )}
    </>
  );
};
